import React, {FC, useCallback} from 'react';
import styled, {css} from 'styled-components';
import {LiveShoppingEvent} from '../../../../../grpc_client/grips/protos/shared/live_shopping_event';
import {bordersColor, dropsYellow} from '../../../../../ui/constants/colors';
import {mediaMaxTablet} from '../../../../../ui/constants/customMedia';
import {fontSize16, fwBold} from '../../../../../ui/constants/typography';
import {useLiveShoppingPlayerApi} from '../../../context/LiveShoppingPlayerProvider';
import {useComputeEventDetails} from '../../hooks/useComputeEventDetails';
import {LiveEventShareButton} from './LiveEventShareButton';

export const LiveEventBanner: FC<{
  event: LiveShoppingEvent;
  teaserImageUrl: string | undefined;
}> = ({event, teaserImageUrl}) => {
  const {
    isLive,
    isEventToday,
    isEnded,
    eventStartMessage,
    isScheduledAfterToday,
  } = useComputeEventDetails(event);
  const {updateShowIdAndAutoplay} = useLiveShoppingPlayerApi();

  const onEventBannerClick = useCallback(() => {
    if (isScheduledAfterToday) {
      return;
    }
    updateShowIdAndAutoplay(event.id);
  }, [isScheduledAfterToday, updateShowIdAndAutoplay, event.id]);

  if (!teaserImageUrl) {
    return null;
  }

  return (
    <EventBanner
      backgroundImageUrl={teaserImageUrl}
      isEnded={isEnded}
      onClick={onEventBannerClick}
      data-testid="LiveShowEventBanner"
    >
      <EventStateMessageContainer
        isLiveOrToday={isLive || (isEventToday && !isEnded)}
      >
        <EventStartMessageText>{eventStartMessage}</EventStartMessageText>
      </EventStateMessageContainer>
      <LiveEventShareButton event={event} />
    </EventBanner>
  );
};

export const EventBanner = styled.div<{
  backgroundImageUrl: string;
  isEnded: boolean;
}>`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  background: url(${props => props.backgroundImageUrl});
  ${props =>
    props.isEnded
      ? css`
          @media ${mediaMaxTablet} {
            background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
              url(${props.backgroundImageUrl});

            background-repeat: no-repeat;
            background-size: cover;
          }
        `
      : ''}
  background-repeat: no-repeat;
  background-size: cover;
  height: 310px;
  width: 100%;
  border-radius: 4px 4px 0 0;
`;

const EventStateMessageContainer = styled.div<{isLiveOrToday: boolean}>`
  position: relative;
  z-index: 0;
  ${props =>
    props.isLiveOrToday
      ? css`
          background-color: ${dropsYellow};
        `
      : css`
          background-color: ${bordersColor};
        `}
  border-radius: 0 0 16px 16px;
  height: 32px;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  ${fontSize16};
  ${fwBold};
  display: flex;
  text-align: center;
`;

const EventStartMessageText = styled.div`
  padding: 8px 12px 9px;
`;
